import gql from 'graphql-tag';
import { CartMainFragment } from '../fragments/cartFragment.mainEndpoint';
import { OrderFragment } from '../stage/cart.mainEndpoint';
import { StoreCityItemFragment } from '../fragments/StoreCityItemFragment.mainEndpoint';

const NotAvailableItemsFragment = gql`
  fragment NotAvailableItemsFragment on CartUnion {
    notAvailableItems {
      cartPromoCodes {
        promoCode {
          id
          promoCodeType {
            id
            title
            identify
          }
          gift {
            id
            title
          }
          product {
            id
          }
          minOrderPrice
          canBeCancelled
          code
          title
          text
          value
          title
        }
      }
      cartTestDrives {
        product {
          id
        }
        testDrive {
          id
          title
          titleForCart
          mainImage
          descriptionForCart
          minOrderPrice
        }
      }
      cartLoyaltyProgram {
        id
        quantity
        product {
          id
          title
        }
        service {
          id
          title
        }
        loyaltyProgram {
          title
          maxQuantityInOrder
          minOrderPrice
          freeDelivery
          comment
          id
          bonusPercentage
        }
      }
    }
  }
`;

export const CartQL = {
  cart: gql`
    query cart($cartToken: String!, $recalculate: Boolean, $strict: Boolean) {
      cart(cartToken: $cartToken, recalculate: $recalculate, strict: $strict) {
        ...CartMainFragment
      }
      servicesAvailableForPurchase(cartToken: $cartToken) {
        service {
          id
          shortDescription
          title
          price
          subscriptionPrice
          subscription_period
          priceWithSubscription
          shortDescriptionWithSubscription
          multiple
          hasDescription
          group
          isTypeSubscription
          maxQuantityInOrder
          __typename
        }
        cartService {
          id
          subscription
          amount
          __typename
        }
        period
        price
        button
        __typename
      }
    }
    ${CartMainFragment}
  `,
  createDigitalOrder: gql`
    mutation createDigitalOrder($data: CreateDigitalOrderInput!) {
      createDigitalOrder(data: $data) {
        success
        error
        warning
        order {
          ...OrderFragment
        }
      }
    }
    ${OrderFragment}
  `,
  getOrderCancelReasons: gql`
    query getOrderCancelReasons {
      getOrderCancelReasons {
        id
        title
        active
        weight
      }
    }
  `,
  getReplacedProductCart: gql`
    query getReplacedProductCart(
      $cartToken: String!
      $recalculate: Boolean
      $strict: Boolean
    ) {
      cart(cartToken: $cartToken, recalculate: $recalculate, strict: $strict) {
        items {
          id
          product {
            comments_number
            rating_comments
            postcardAllowed
            isPostponed
            id
            war_id
            forbid_discount
            superPrice
            title
            package
            price
            oldPrice
            maxPurchase
            mainImage
            weightOfGoods
            weightOfPacking
            mainRootRubric {
              id
              title
              shortTitle
              short_title
              identify
            }
            gds_count
            isAvailableForPurchase

            catalogAnalogs {
              id
              title
              oldPriceField
              currentPriceField
              mainImage

              oldPriceField2
              currentPriceField2

              comments_number
              rating_comments
              weight_of_goods

              measureItem {
                weight
                symbol
              }

              priceBlockWeightPrice
              vesovoi
            }
          }
          amount
          purchasePrice
          specialPrice
          discount
          freeOfCharge
        }
      }
    }
  `,
  addMultipleProductsToCart: gql`
    mutation addMultipleProductsToCart(
      $cartToken: String!
      $productsToAddToCart: [AddProductToCartInput!]!
      $continueAnyway: Boolean
    ) {
      addMultipleProductsToCart(
        cartToken: $cartToken
        productsToAddToCart: $productsToAddToCart
        continueAnyway: $continueAnyway
      ) {
        success
        error
        warning
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  addProductToCart: gql`
    mutation addProductToCart(
      $cartToken: String!
      $productId: Int!
      $quantity: Int!
      $continueAnyway: Boolean
      $cartItemId: Int
    ) {
      addProductToCart(
        cartToken: $cartToken
        productId: $productId
        quantity: $quantity
        continueAnyway: $continueAnyway
        cartItemId: $cartItemId
      ) {
        success
        error
        warning
        cart {
          ...CartMainFragment
        }
        cartItem {
          amount
          id
          postcards {
            id
            cartItem {
              id
            }
            sender
            text
          }
          product {
            id
            cartItemId
            postcardAllowed
            specialConditions
            title
            price
            mainRootRubric {
              title
            }
          }
        }
        ...NotAvailableItemsFragment
      }
    }
    ${CartMainFragment}
    ${NotAvailableItemsFragment}
  `,
  removeCartItem: gql`
    mutation removeCartItem(
      $cartToken: String!
      $cartItemId: Int!
      $continueAnyway: Boolean
    ) {
      removeCartItem(
        cartToken: $cartToken
        cartItemId: $cartItemId
        continueAnyway: $continueAnyway
      ) {
        success
        error
        warning
        cart {
          ...CartMainFragment
        }
        ...NotAvailableItemsFragment
      }
    }
    ${CartMainFragment}
    ${NotAvailableItemsFragment}
  `,
  addProductToPostponed: gql`
    mutation addProductToPostponedList($productId: Int!) {
      addProductToPostponedList(productId: $productId) {
        success
        error
        quantity
      }
    }
  `,
  removeProductFromPostponed: gql`
    mutation removeProductFromPostponedList($productId: Int!) {
      removeProductFromPostponedList(productId: $productId) {
        success
        error
        quantity
      }
    }
  `,
  clearCart: gql`
    mutation clearCart($cartToken: String!) {
      clearCart(cartToken: $cartToken) {
        success
        error
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  removeCartService: gql`
    mutation removeCartService(
      $cartToken: String!
      $id: Int!
      $continueAnyway: Boolean
    ) {
      removeCartService(
        cartToken: $cartToken
        cartServiceId: $id
        continueAnyway: $continueAnyway
      ) {
        success
        error
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  addServiceToCart: gql`
    mutation addServiceToCart($cartToken: String!, $id: Int!, $quantity: Int) {
      addServiceToCart(
        cartToken: $cartToken
        serviceId: $id
        quantity: $quantity
      ) {
        success
        error
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  payWithBonuses: gql`
    mutation payOrderWithBonuses($cartToken: String!, $amount: Float!) {
      payOrderWithBonuses(cartToken: $cartToken, amount: $amount) {
        success
        error
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  applyPromoCode: gql`
    mutation applyPromoCode($cartToken: String!, $code: String!) {
      applyPromoCode(cartToken: $cartToken, code: $code) {
        success
        error
        errorCode
        identify
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  removeCartPromoCode: gql`
    mutation removeCartPromoCode($cartToken: String!, $cartPromoCodeId: Int!) {
      removeCartPromoCode(
        cartToken: $cartToken
        cartPromoCodeId: $cartPromoCodeId
      ) {
        success
        error
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  applicationForParticipationInTestDrive: gql`
    mutation applicationForParticipationInTestDrive(
      $cartToken: String!
      $testDriveId: Int!
    ) {
      applicationForParticipationInTestDrive(
        cartToken: $cartToken
        testDriveId: $testDriveId
      ) {
        success
        error
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  cancelTestDriveApplication: gql`
    mutation cancelTestDriveApplication(
      $cartToken: String!
      $testDriveId: Int!
    ) {
      cancelTestDriveApplication(
        cartToken: $cartToken
        testDriveId: $testDriveId
      ) {
        success
        error
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  shareCart: gql`
    query shareCart($cartToken: String!) {
      shareCart(cartToken: $cartToken) {
        success
        error
        cartShare {
          id
          code
          url
        }
      }
    }
  `,
  addCartItemPostcard: gql`
    mutation addCartItemPostcard(
      $cartToken: String!
      $postcards: [CartPostcardInput!]!
    ) {
      addCartItemPostcard(cartToken: $cartToken, postcards: $postcards) {
        success
        error
        cart {
          ...CartMainFragment
        }
      }
    }
    ${CartMainFragment}
  `,
  clearCookie: gql`
    query clearCookie {
      clearCookie
    }
  `,
  updateOrCreateMenuCart: gql`
    mutation updateOrCreateMenuCart(
      $store_id: Int!
      $cartToken: String
      $menu_people_id: Int
    ) {
      updateOrCreateMenuCart(
        store_id: $store_id
        cartToken: $cartToken
        menu_people_id: $menu_people_id
      ) {
        ...CartMainFragment
      }
    }
    ${CartMainFragment}
  `,
  createMenuOrderFromCart: gql`
    mutation createMenuOrderFromCart(
      $cartToken: String!
      $table: Int
      $phone: String
    ) {
      createMenuOrderFromCart(
        cartToken: $cartToken
        table: $table
        phone: $phone
      ) {
        success
        error
        warning
        order {
          ...OrderFragment
        }
      }
    }
    ${OrderFragment}
  `,
  getActiveStoreCityWithFields: gql`
    query getActiveStoreCityWithFields {
      getActiveStoreCitiesWithFields {
        ...StoreCityItemFragment
      }
    }
    ${StoreCityItemFragment}
  `
};
